// frontend/src/services/staffScheduleFixtures.js
//
// Dev-mode inline fixtures for SCHED-SLICE1-G staff schedule view.
//
// Gated by `localStorage.SCHED_MY_SCHEDULE_DEV_FIXTURES === 'true'` — pure-
// additive, no prod impact. Used while Simone's SCHED-SLICE1-D staff
// endpoint `GET /api/workforce/my-shifts` is being authored.
//
// Once the live endpoint ships, set the localStorage key to anything else
// (or remove it) and the fixture path is bypassed in api.js.
//
// The fixtures intentionally exercise every visible state:
//   - Multiple shifts in a week (calendar grid populated)
//   - Past shift (70% opacity treatment per visual spec §9.2)
//   - Empty-week state (when localStorage flag === 'empty')
//   - No-published-schedule state (when localStorage flag === 'no-schedule')
//   - Upcoming-in-N-minutes shift (gold banner test path)
//   - Recent updatedAt (schedule-changed banner test path)
//
// All ISO timestamps are -07:00 (Phoenix-MST; no DST in AZ).

import moment from "moment-timezone";

const STUDIO_TZ = "America/Phoenix";

/**
 * Construct an ISO timestamp for the given weekStart + dayIndex + HH:mm.
 *
 * @param {string} weekStart YYYY-MM-DD (Sunday-start)
 * @param {number} dayIndex  0-6 (Sun-Sat)
 * @param {string} time      HH:mm 24h
 */
const dayTime = (weekStart, dayIndex, time) => {
  return moment.tz(`${weekStart} ${time}`, "YYYY-MM-DD HH:mm", STUDIO_TZ)
    .add(dayIndex, "days")
    .toISOString();
};

/**
 * Return a deterministic fixture set for a given weekStart.
 *
 * Dev mode supports three banner variants via the localStorage flag value:
 *   - 'true'         -> standard fixture (multiple shifts)
 *   - 'empty'        -> staff has zero shifts this week (schedule IS published)
 *   - 'no-schedule'  -> entire week has no published schedule yet
 *   - 'starts-soon'  -> includes a shift starting in 45 minutes from now
 *
 * @param {{ weekStart: string }} args
 * @param {string} variant
 */
export const getMyScheduleFixture = ({ weekStart } = {}, variant = "true") => {
  // Sanity-default the weekStart.
  const ws = weekStart || moment.tz(STUDIO_TZ).startOf("week").format("YYYY-MM-DD");

  if (variant === "no-schedule") {
    return {
      shifts: [],
      cancelledShifts: [],
      schedulePublishedForWeek: false,
    };
  }

  if (variant === "empty") {
    return {
      shifts: [],
      cancelledShifts: [],
      schedulePublishedForWeek: true,
    };
  }

  // Standard fixture — a couple shifts including a past one and a future one.
  const baseShifts = [
    {
      _id: "fixture_shift_001",
      staffProfileId: "staff_jamie",
      startTime: dayTime(ws, 1, "08:00"), // Mon
      endTime:   dayTime(ws, 1, "14:00"),
      roleCode: "front_desk",
      status: "published",
      notes: null,
      weekStartDate: ws,
      updatedAt: "2026-04-19T10:32:00-07:00",
    },
    {
      _id: "fixture_shift_002",
      staffProfileId: "staff_jamie",
      startTime: dayTime(ws, 2, "08:00"), // Tue
      endTime:   dayTime(ws, 2, "14:00"),
      roleCode: "front_desk",
      status: "published",
      notes: "Cover for Sarah's training session.",
      weekStartDate: ws,
      updatedAt: "2026-04-18T17:00:00-07:00",
    },
    {
      _id: "fixture_shift_003",
      staffProfileId: "staff_jamie",
      startTime: dayTime(ws, 4, "09:00"), // Thu
      endTime:   dayTime(ws, 4, "13:00"),
      roleCode: "massage_therapist",
      status: "published",
      notes: null,
      weekStartDate: ws,
      updatedAt: "2026-04-18T17:00:00-07:00",
    },
  ];

  if (variant === "starts-soon") {
    // Inject a "starts in ~45 minutes" shift for banner testing.
    const startsSoon = moment.tz(STUDIO_TZ).add(45, "minutes");
    const endsSoon = startsSoon.clone().add(4, "hours");
    baseShifts.push({
      _id: "fixture_shift_starts_soon",
      staffProfileId: "staff_jamie",
      startTime: startsSoon.toISOString(),
      endTime: endsSoon.toISOString(),
      roleCode: "front_desk",
      status: "published",
      notes: null,
      weekStartDate: ws,
      updatedAt: "2026-04-19T10:32:00-07:00",
    });
  }

  return {
    shifts: baseShifts,
    cancelledShifts: [],
    schedulePublishedForWeek: true,
  };
};
